import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Phone, ExternalLink, ShieldAlert, Loader2 } from 'lucide-react';

/**
 * MoveraCareChatbot — public-website transportation assistant.
 *
 * Locked constraints (do not relax):
 *   - Rule-based only. No LLM, no fare estimates, no eligibility decisions,
 *     no medical advice.
 *   - No lead capture in this widget. No name / email / phone / medical / PHI.
 *   - Hidden on /dispatch/*, /admin, and any authenticated surface.
 *   - Actions link to the existing secure Book a Ride, Call Dispatch, and
 *     Contact Us surfaces; never duplicates those forms.
 *
 * Public API:
 *   GET  /api/chatbot/topics        -> suggested-question chips
 *   POST /api/chatbot/message       -> {reply, actions, is_emergency, note}
 */

const STORAGE_SESSION_KEY = 'mc_chatbot_session_v1';
const STORAGE_HISTORY_KEY = 'mc_chatbot_history_v1';
const STORAGE_OPEN_KEY = 'mc_chatbot_open_v1';
const MAX_HISTORY = 40;

/** Paths where the widget must NOT render. */
const EXCLUDED_PREFIXES = ['/dispatch', '/admin'];

function shouldExclude(pathname) {
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function getOrCreateSessionId() {
  try {
    const existing = sessionStorage.getItem(STORAGE_SESSION_KEY);
    if (existing) return existing;
    const id = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(STORAGE_SESSION_KEY, id);
    return id;
  } catch {
    return `s-${Date.now()}`;
  }
}

function loadHistory() {
  try {
    const raw = sessionStorage.getItem(STORAGE_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(msgs) {
  try {
    sessionStorage.setItem(STORAGE_HISTORY_KEY, JSON.stringify(msgs.slice(-MAX_HISTORY)));
  } catch { /* ignore quota */ }
}

const WELCOME = {
  role: 'assistant',
  text:
    "Hi! I'm the MoveraCare Transportation Assistant. I can answer common questions about our non-emergency medical transportation service and point you to the right form. What can I help with?",
  actions: [
    { kind: 'link', label: 'Book a Ride', href: '/book-ride' },
    { kind: 'tel', label: 'Call Dispatch · 832-925-2128', href: 'tel:+18329252128' },
    { kind: 'link', label: 'Contact Us', href: '/contact' },
  ],
};

export default function MoveraCareChatbot() {
  const location = useLocation();
  const isExcluded = shouldExclude(location.pathname);

  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_OPEN_KEY) === '1'; } catch { return false; }
  });
  const [topics, setTopics] = useState([]);
  const [messages, setMessages] = useState(() => {
    const hist = loadHistory();
    return hist.length ? hist : [WELCOME];
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Persist open state and history
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_OPEN_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  }, [open]);
  useEffect(() => { saveHistory(messages); }, [messages]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Focus input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Fetch topics once
  useEffect(() => {
    if (isExcluded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chatbot/topics`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTopics(data.topics || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [isExcluded]);

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    const userMsg = { role: 'user', text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errText =
          res.status === 429
            ? "You're sending messages a bit quickly. Please wait a moment and try again."
            : data.detail || 'Sorry, something went wrong. Please try again.';
        setMessages((prev) => [...prev, { role: 'assistant', text: errText, actions: [] }]);
        return;
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data.reply,
          actions: data.actions || [],
          note: data.note || null,
          is_emergency: !!data.is_emergency,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Network issue — please try again in a moment.', actions: [] },
      ]);
    } finally {
      setSending(false);
    }
  }, [sessionId, sending]);

  const handleSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const handleTopic = (label) => send(label);

  const clearChat = () => {
    setMessages([WELCOME]);
    try { sessionStorage.removeItem(STORAGE_HISTORY_KEY); } catch { /* ignore */ }
  };

  if (isExcluded) return null;

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chatbot-launcher"
          aria-label="Open MoveraCare Transportation Assistant"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 rounded-full bg-[#004C97] px-4 py-3 text-white shadow-xl hover:bg-[#003a78] focus:outline-none focus:ring-2 focus:ring-[#F9A825] focus:ring-offset-2 transition-all"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline text-sm font-semibold">Chat with us</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="MoveraCare Transportation Assistant"
          data-testid="chatbot-panel"
          className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 z-40 flex flex-col rounded-2xl bg-white shadow-2xl border border-[#004C97]/20 sm:w-[380px] max-h-[85vh] sm:max-h-[600px] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#004C97] px-4 py-3 text-white">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">MoveraCare Assistant</p>
              <p className="text-[11px] text-white/70 truncate">Non-emergency transport help · Not for PHI</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={clearChat}
                data-testid="chatbot-clear-btn"
                className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white text-[11px] font-medium"
                title="Clear conversation"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="chatbot-close-btn"
                className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" data-testid="chatbot-messages">
            {messages.map((m, idx) => (
              <MessageBubble key={idx} msg={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="chatbot-typing">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Assistant is typing…</span>
              </div>
            )}
          </div>

          {/* Suggested questions */}
          {topics.length > 0 && messages.length <= 1 && (
            <div className="border-t border-gray-100 bg-white px-3 py-2" data-testid="chatbot-topics">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                Suggested questions
              </p>
              <div className="flex flex-wrap gap-1.5">
                {topics.slice(0, 6).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleTopic(t.label)}
                    data-testid={`chatbot-topic-${t.id}`}
                    className="text-[12px] px-2.5 py-1 rounded-full border border-[#004C97]/20 bg-white text-[#004C97] hover:bg-[#004C97] hover:text-white transition-colors"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Composer */}
          <form onSubmit={handleSubmit} className="border-t border-gray-200 p-2 bg-white" data-testid="chatbot-form">
            <label htmlFor="chatbot-input" className="sr-only">Message the MoveraCare assistant</label>
            <div className="flex items-center gap-1.5">
              <input
                ref={inputRef}
                id="chatbot-input"
                data-testid="chatbot-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about services, hours, booking…"
                maxLength={500}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F9A825] focus:border-transparent"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                data-testid="chatbot-send-btn"
                className="rounded-lg bg-[#F9A825] p-2 text-white hover:bg-[#E08E1E] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              Do not share personal or medical information here.
            </p>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  if (msg.is_emergency) {
    return (
      <div
        className="rounded-lg border border-red-300 bg-red-50 p-3 flex items-start gap-2"
        data-testid="chatbot-emergency-msg"
      >
        <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-red-900 font-semibold">{msg.text}</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      data-testid={isUser ? 'chatbot-user-msg' : 'chatbot-assistant-msg'}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'bg-[#004C97] text-white rounded-br-sm'
            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
        }`}
      >
        <p className="whitespace-pre-wrap">{msg.text}</p>
        {msg.note && (
          <p className="mt-2 text-[11px] italic text-gray-600 border-t border-gray-100 pt-1.5">
            {msg.note}
          </p>
        )}
        {!isUser && msg.actions && msg.actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="chatbot-actions">
            {msg.actions.map((a, i) => (
              <ActionButton key={i} action={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionButton({ action }) {
  const isTel = action.kind === 'tel';
  const Icon = isTel ? Phone : ExternalLink;
  const testid = isTel
    ? 'chatbot-action-call'
    : action.href === '/book-ride'
    ? 'chatbot-action-book'
    : action.href === '/contact'
    ? 'chatbot-action-contact'
    : 'chatbot-action-link';

  return (
    <a
      href={action.href}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-semibold border ${
        isTel
          ? 'bg-[#F9A825] text-white border-[#F9A825] hover:bg-[#E08E1E]'
          : 'bg-white text-[#004C97] border-[#004C97]/30 hover:bg-[#004C97] hover:text-white'
      } transition-colors`}
    >
      <Icon className="w-3 h-3" />
      {action.label}
    </a>
  );
}
